<template>
  <div class="min-h-screen bg-white relative">
    <!-- Header Component for Cluster -->
    <ClusterHeader 
      :currentSite="'cluster'" 
      :activeSection="activeSection"
      @toggleMobileMenu="handleToggleMobileMenu"
      @openContactModal="handleOpenContactModal"
      @openLoginModal="handleOpenLoginModal"
      @smoothScrollTo="handleSmoothScrollTo"
    />
    
    <!-- Content slot for cluster pages -->
    <slot />

    <!-- Footer Component -->
    <SiteFooter />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import SiteFooter from '~/layouts/theme/custom/clustersie/components/SiteFooter.vue'
import ClusterHeader from '~/layouts/theme/custom/clustersie/components/ClusterHeader.vue'

// Define props to receive theme data from parent
const props = defineProps({
  theme: {
    type: Object,
    default: () => ({})
  }
})

// State management
const activeSection = ref('cluster')
const isMobileMenuOpen = ref(false)
const isContactModalOpen = ref(false)
const isLoginModalOpen = ref(false)

// Log received theme for debugging
console.log('ClusterSite received theme:', props.theme)

// Handle events from header component
const handleToggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const handleOpenContactModal = () => {
  isContactModalOpen.value = true
}

const handleOpenLoginModal = () => {
  isLoginModalOpen.value = true
}

const handleSmoothScrollTo = (target, event) => {
  smoothScrollTo(target, event)
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

// Contact modal functions
const openContactModal = () => {
  isContactModalOpen.value = true
  closeMobileMenu()
}

const closeContactModal = () => {
  isContactModalOpen.value = false
}

// Smooth scroll function for navigation
const smoothScrollTo = (targetId, event) => {
  event?.preventDefault()
  
  // Close mobile menu immediately if open
  closeMobileMenu()
  
  // Handle home section - scroll to top
  if (targetId === 'home' || targetId === 'cluster') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    return
  }
  
  // Handle other sections
  const target = document.getElementById(targetId)
  if (!target) {
    return
  }
  
  // Calculate offset for sticky header
  const headerHeight = 80
  const targetPosition = target.offsetTop - headerHeight
  
  // Use native smooth scroll for immediate response
  window.scrollTo({ 
    top: targetPosition, 
    behavior: 'smooth' 
  })
}

// Scroll detection for active section
const handleScroll = () => {
  const currentScrollY = window.scrollY
  const sections = ['cluster', 'dashboard', 'manage', 'products', 'reports']
  const scrollPos = currentScrollY + 100 // offset for sticky header

  // Check if we're at the top of the page (cluster section)
  if (currentScrollY < 50) {
    activeSection.value = 'cluster'
    return
  }

  // Find active section based on scroll position
  for (let i = sections.length - 1; i >= 0; i--) {
    if (sections[i] === 'cluster') continue // Skip cluster as we handle it above
    
    const section = document.getElementById(sections[i])
    if (section && section.offsetTop <= scrollPos) {
      activeSection.value = sections[i]
      break
    }
  }
}

onMounted(() => {
  // Add scroll listener
  window.addEventListener('scroll', handleScroll)
  
  // Initial check
  handleScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
/* Cluster site specific styles */
.bg-gradient-to-br {
  background: linear-gradient(to bottom right, #f0fdf4, #eff6ff);
}

/* Smooth scroll for one-page navigation */
html {
  scroll-behavior: smooth;
}

/* Adjust scroll position for fixed header */
section {
  scroll-margin-top: 80px;
}
</style>

<style>
/* Global styles for smooth scroll and fixed navigation */
html {
  scroll-behavior: smooth !important;
}

/* Ensure navigation works globally */
body {
  overflow-x: hidden;
  position: relative;
}
</style>